import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { isEmpty } from 'lodash';
import * as Sentry from '@sentry/browser';
import { updateAuthData } from 'split-auth/build/redux/auth/authData/actions';
import Cookies from 'js-cookie';

import Routes from './pages';
import Layout from './components/Layout';
import { ENVIRONMENT } from './config';
import { getAccountRequest } from './sagas/account/actions';
import { getNotificationsRequest } from './sagas/notifications/actions';
import { getSwaggerRequest } from './sagas/swagger/actions';
import { tokenAuthRequest } from './sagas/tokenAuth/actions';
import { getSendbirdTokenRequest } from './sagas/sendbird/token/actions';
import { getAiChatTokenRequest } from './sagas/aiChatToken/actions';
import { getAiPromptSuggestionsRequest } from './sagas/aiPromptSuggestions/actions';
import { getBaseCommissionRateRequest } from './sagas/baseCommissionRate/actions';
import { getBillingCreditsBalanceRequest } from './sagas/billing/creditsBalance/actions';
import { getStripeBillingRequest } from './sagas/stripeBilling/actions';
import { resize } from './utils/onResize';
import { getParameterByName, removeFromQueryString } from './utils/queryString';
import { setParameters } from './utils/setLoginParameters';
import { routes } from './pages';
import './styles/_main.scss';
import { getSettingsRequest } from './sagas/settings/actions';
import { SendBirdProvider } from '@sendbird/uikit-react';
import { useMediaQuery } from '@mui/material';
import { getBillingSubscriptionInfoRequest } from './sagas/billing/subscriptionInfo/actions';
import { getShopifyStatusRequest } from './sagas/shopify/status/actions';

console.log(ENVIRONMENT);

const App = () => {
  const dispatch = useDispatch();
  const { push, location } = useHistory();
  const matches = useMediaQuery('(min-width:814px)');

  const account = useSelector(s => s.account);
  const notifications = useSelector(s => s.notifications);
  const authData = useSelector(s => s.authData);
  const sendbirdTokenState = useSelector(s => s.getSendbirdToken);
  const settingsState = useSelector(s => s.settings);

  const data = account?.data || {};
  const filters = notifications?.filters;

  const access_token = sendbirdTokenState?.data?.access_token || null;
  const uuid = sendbirdTokenState?.data?.uuid || null;
  const sendbird_app_id = settingsState?.data?.sendbird_app_id || null;

  const isSendbirdReady = sendbirdTokenState?.isReady || false;

  useEffect(() => {
    dispatch(getSettingsRequest());

    const oneTimeToken = getParameterByName('auth_token', window.location.href);

    if (oneTimeToken) {
      const previousOneTimeToken = Cookies.get('prev_one_time_token');

      if (oneTimeToken !== previousOneTimeToken) {
        Cookies.set('prev_one_time_token', oneTimeToken);

        dispatch(tokenAuthRequest({
          values: { auth_token: oneTimeToken },
          onSuccess: responseData => {
            setParameters(responseData);
            dispatch(updateAuthData({ ...authData, api_token: responseData.api_token }));
            dispatch(getSendbirdTokenRequest({}));
            getData();

            redirect();

            try {
              navigator.serviceWorker
                .getRegistrations()
                .then(registrations => {
                  for (let registration of registrations) {
                    registration.unregister();
                  }
                })
                .then(() => window.location.reload());
            } catch (error) {
              console.log(error);
            }
          }
        }));
      } else {
        redirect();
        noOneTimeToken();
      }
    } else {
      redirect();
      noOneTimeToken();
    }

    resize();
    const handleResize = () => resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!isEmpty(data)) {
      Sentry.configureScope(scope => scope.setUser({ id: data.id, email: data.email }));

      if (window.Intercom) {
        window.Intercom('update', {
          email: data.email,
          user_id: data.uuid,
          ...(data.first_name && data.last_name && { name: `${data.first_name} ${data.last_name}` })
        });
      }
    }
  }, [data]); // eslint-disable-line

  const redirect = () => {
    window.history.replaceState({}, '', `${window.location.pathname}${removeFromQueryString('auth_token')}`);
  };

  const noOneTimeToken = () => {
    if (ENVIRONMENT !== 'development') {
      const token = Cookies.get('bsn_tkn');

      if (token) {
        Cookies.remove('bsn_tkn');
        Cookies.set('bsn_tkn', token, { expires: 30 });

        dispatch(updateAuthData({ ...authData, api_token: token }));
        getData();
        dispatch(getSendbirdTokenRequest({}));
      }
    }

    const authTokenCookie = Cookies.get('bsn_tkn');

    if (authTokenCookie) {
      dispatch(updateAuthData({ ...authData, api_token: authTokenCookie }));
      getData();
      dispatch(getSendbirdTokenRequest({}));
    } else if (location.pathname === routes.acceptInvitation.business.path) {
      dispatch(getSwaggerRequest());
    } else {
      push(routes.login.business.link());
    }
  };

  const getData = () => {
    dispatch(getAccountRequest({}));
    dispatch(getNotificationsRequest({ filters }));
    dispatch(getSwaggerRequest());
    dispatch(getShopifyStatusRequest({}));
    dispatch(getBaseCommissionRateRequest({}));
    dispatch(getBillingCreditsBalanceRequest({}));
    dispatch(getStripeBillingRequest({}));
    dispatch(getAiChatTokenRequest());
    dispatch(getAiPromptSuggestionsRequest());
    dispatch(getBillingSubscriptionInfoRequest({}));
  };

  return (
    <div>
      {isSendbirdReady ? (
        <SendBirdProvider
          key={`${sendbird_app_id}-${uuid}`}
          appId={sendbird_app_id}
          userId={uuid}
          accessToken={access_token}
          breakpoint={!matches}
        >
          <Layout sendbirdReady>
            <Routes />
          </Layout>
        </SendBirdProvider>
      ) : (
        <Layout sendbirdReady={false}>
          <Routes />
        </Layout>
      )}
    </div>
  );
};

export default App;